import { useEffect, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useCreateUpsellRequest, { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

import BaseModal from './BaseModal'
import ErrorStep from './ErrorStep'
import FirstStep from './FirstStep'
import SecondStep from './SecondStep'
import SuccessStep from './SuccessStep'
import SurveyStep from './SurveyStep'

type CheckoutModalProps = {
  isOpen: boolean
  onClose: () => void
  moduleName: AvailableModules
  moduleSlug: string
  from: string
}

const CheckoutModal = ({ isOpen, onClose, moduleName, moduleSlug, from }: CheckoutModalProps) => {
  const [step, setStep] = useState('first')
  const { allAddons, shouldBypassSelfUpsell, allCrossSellingAddons } = useSelfUpsellContext()
  const [selectedAddonId, setSelectedAddonId] = useState(
    allAddons.f25Addons[0]?.internalTitle || ''
  )

  const [selectedCrossSellingAddonId, setSelectedCrossSellingAddonId] = useState(
    allCrossSellingAddons.f25Addons[0]?.internalTitle || ''
  )

  const addon = useMemo(() => {
    return (
      allAddons.f25Addons.find((a) => a.internalTitle === selectedAddonId) || allAddons.f25Addons[0]
    )
  }, [allAddons.f25Addons, selectedAddonId])

  const crossSellingAddon = useMemo(() => {
    return (
      allCrossSellingAddons.f25Addons.find(
        (a) => a.internalTitle === selectedCrossSellingAddonId
      ) || allCrossSellingAddons.f25Addons[0]
    )
  }, [allCrossSellingAddons.f25Addons, selectedCrossSellingAddonId])

  const { create } = useCreateUpsellRequest()

  const handleRequestUpsell = () => {
    if (shouldBypassSelfUpsell) return
    create({
      requestSource: 'self-upsell_marketplace',
      moduleRequested: moduleName,
      requestReason: ValidReasons.MissingAddon,
      upsellRequestStatus: 'Opportunity detected',
      upsellRequestComponentType: 'self_upsell_modal',
    })
  }

  const handleClose = () => {
    track(trackingEvents.upsell.closeSelfUpsellModal, {
      module: moduleName,
      step: step,
    })
    if (step === 'survey') {
      onClose()
      setStep('first')
      return
    }
    if (step === 'success') {
      onClose()
      window.location.reload()
      return
    }
    if (step !== 'success' && !shouldBypassSelfUpsell) {
      handleRequestUpsell()
      onClose()
      setStep('survey')
    } else {
      onClose()
      setStep('first')
    }
  }

  const handleAssistance = () => {
    track(trackingEvents.upsell.clickRequestAssistanceButton, {
      module: moduleName,
      step: step,
    })
    app.setFlash({
      type: 'correct',
      message: i18n.t('upselling_kit.upselling_button.success_message.title'),
    })
    handleClose()
  }

  useEffect(() => {
    if (isOpen) {
      track(trackingEvents.upsell.viewSelfUpsellModal, {
        module: moduleName,
        from,
      })
    }
  }, [isOpen])

  if (!addon) return null

  return (
    <>
      <BaseModal isOpen={isOpen} onClose={handleClose} width="760px" isLocked>
        <Box
          style={{
            minHeight: '560px',
            maxHeight: '700px',
          }}
        >
          {step === 'first' && (
            <FirstStep
              addon={addon}
              crossSellingAddon={crossSellingAddon}
              step={step}
              setStep={setStep}
              setSelectedAddonId={setSelectedAddonId}
              setSelectedCrossSellingAddonId={setSelectedCrossSellingAddonId}
              handleAssistance={handleAssistance}
              moduleName={moduleName}
              moduleSlug={moduleSlug}
            />
          )}
          {step === 'second' && (
            <SecondStep
              addon={addon}
              crossSellingAddon={crossSellingAddon}
              step={step}
              setStep={setStep}
              moduleName={moduleName}
            />
          )}
          {step === 'success' && (
            <SuccessStep addon={addon} step={step} onClose={handleClose} moduleName={moduleName} />
          )}
          {step === 'error' && (
            <ErrorStep
              addon={addon}
              step={step}
              onClose={handleClose}
              handleAssistance={handleAssistance}
              moduleName={moduleName}
            />
          )}
        </Box>
      </BaseModal>

      {step === 'survey' && (
        <SurveyStep
          moduleName={moduleName}
          moduleSlug={moduleSlug}
          onClose={() => {
            setStep('first')
            onClose()
          }}
        />
      )}
    </>
  )
}

export default CheckoutModal
